import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DateTravelerStep = ({ onNext, bookingData, setBookingData }) => {
  const [selectedDate, setSelectedDate] = useState(bookingData?.selectedDate || '');
  const [selectedTime, setSelectedTime] = useState(bookingData?.selectedTime || '');
  const [adults, setAdults] = useState(bookingData?.adults || 2);
  const [children, setChildren] = useState(bookingData?.children || 0);
  const [infants, setInfants] = useState(bookingData?.infants || 0);
  const [errors, setErrors] = useState({});

  const timeSlots = [
    { value: '09:00', label: '9:00 AM - Morning Tour' },
    { value: '14:00', label: '2:00 PM - Afternoon Tour' },
    { value: '18:00', label: '6:00 PM - Evening Tour' }
  ];

  const availableDates = [
    '2025-01-20',
    '2025-01-21',
    '2025-01-22',
    '2025-01-23',
    '2025-01-24',
    '2025-01-25',
    '2025-01-26',
    '2025-01-27',
    '2025-01-28'
  ];

  const handleCounterChange = (type, operation) => {
    const setters = {
      adults: setAdults,
      children: setChildren,
      infants: setInfants
    };
    
    const values = { adults, children, infants };
    const currentValue = values?.[type];
    
    if (operation === 'increment') {
      setters?.[type](currentValue + 1);
    } else if (operation === 'decrement' && currentValue > 0) {
      if (type === 'adults' && currentValue > 1) {
        setters?.[type](currentValue - 1);
      } else if (type !== 'adults') {
        setters?.[type](currentValue - 1);
      }
    }
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (!selectedDate) {
      newErrors.date = 'Please select a date';
    }
    
    if (!selectedTime) {
      newErrors.time = 'Please select a time slot';
    }
    
    if (adults < 1) {
      newErrors.adults = 'At least 1 adult is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setBookingData({
        ...bookingData,
        selectedDate,
        selectedTime,
        adults,
        children,
        infants
      });
      onNext();
    }
  };

  const getTotalTravelers = () => adults + children + infants;
  const getBasePrice = () => 299;
  const getTotalPrice = () => {
    const basePrice = getBasePrice();
    const adultPrice = adults * basePrice;
    const childPrice = children * (basePrice * 0.7);
    const infantPrice = infants * (basePrice * 0.1);
    return adultPrice + childPrice + infantPrice;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Tour Summary */}
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-start space-x-4">
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted">
                <img
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400"
                  alt="Bali Cultural Heritage Tour"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Bali Cultural Heritage Tour
                </h2>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={16} />
                    <span>8 hours</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={16} />
                    <span>Small group</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} />
                    <span>4.8 (124 reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Date Selection */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Select Date & Time
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Input
                  label="Tour Date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                  error={errors?.date}
                  required
                />
                {selectedDate && (
                  <div className="mt-2 p-3 bg-primary/5 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="Calendar" size={16} className="text-primary" />
                      <span className="text-primary font-medium">
                        {availableDates?.includes(selectedDate) ? 'Available' : 'Limited availability'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <Select
                  label="Time Slot"
                  options={timeSlots}
                  value={selectedTime}
                  onChange={setSelectedTime}
                  placeholder="Choose time"
                  error={errors?.time}
                  required
                />
              </div>
            </div>
          </div>

          {/* Traveler Selection */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Number of Travelers
            </h3>
            
            <div className="space-y-4">
              {/* Adults */}
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div>
                  <h4 className="font-medium text-foreground">Adults</h4>
                  <p className="text-sm text-muted-foreground">Age 18+</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('adults', 'decrement')}
                    disabled={adults <= 1}
                    className="w-10 h-10"
                  >
                    <Icon name="Minus" size={16} />
                  </Button>
                  <span className="w-8 text-center font-medium">{adults}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('adults', 'increment')}
                    className="w-10 h-10"
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>
              </div>

              {/* Children */}
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div>
                  <h4 className="font-medium text-foreground">Children</h4>
                  <p className="text-sm text-muted-foreground">Age 3-17</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('children', 'decrement')}
                    disabled={children <= 0}
                    className="w-10 h-10"
                  >
                    <Icon name="Minus" size={16} />
                  </Button>
                  <span className="w-8 text-center font-medium">{children}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('children', 'increment')}
                    className="w-10 h-10"
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>
              </div>

              {/* Infants */}
              <div className="flex items-center justify-between py-4">
                <div>
                  <h4 className="font-medium text-foreground">Infants</h4>
                  <p className="text-sm text-muted-foreground">Age 0-2</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('infants', 'decrement')}
                    disabled={infants <= 0}
                    className="w-10 h-10"
                  >
                    <Icon name="Minus" size={16} />
                  </Button>
                  <span className="w-8 text-center font-medium">{infants}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCounterChange('infants', 'increment')}
                    className="w-10 h-10"
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {errors?.adults && (
              <p className="text-sm text-error mt-2">{errors?.adults}</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-border p-6 sticky top-32">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Booking Summary
            </h3>
            
            <div className="space-y-4">
              {selectedDate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-medium">
                    {new Date(selectedDate)?.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
              
              {selectedTime && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Time:</span>
                  <span className="font-medium">
                    {timeSlots?.find(slot => slot?.value === selectedTime)?.label?.split(' - ')?.[0]}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Travelers:</span>
                <span className="font-medium">{getTotalTravelers()}</span>
              </div>
              
              <div className="border-t border-border pt-4 space-y-2">
                {adults > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Adults × {adults}</span>
                    <span className="font-medium">${(adults * getBasePrice())?.toFixed(2)}</span>
                  </div>
                )}
                
                {children > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Children × {children}</span>
                    <span className="font-medium">${(children * getBasePrice() * 0.7)?.toFixed(2)}</span>
                  </div>
                )}
                
                {infants > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Infants × {infants}</span>
                    <span className="font-medium">${(infants * getBasePrice() * 0.1)?.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-foreground">Total:</span>
                  <span className="text-xl font-bold text-primary">${getTotalPrice()?.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleNext}
              className="w-full mt-6"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continue to Add-ons
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateTravelerStep;